import { alpha, Box, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ErrorTriangleIcon } from '#shared';

import { useAutoScroll } from '../model';

import type { AppDataErrors } from '#shared';

const PREFIX = 'TSMED-DataErrorsList';
export const dataErrorsListClasses = {
  wrapper: `${PREFIX}-wrapper`,
  wrapperFadeBefore: `${PREFIX}-wrapperFadeBefore`,
  wrapperFadeAfter: `${PREFIX}-wrapperFadeAfter`,
  list: `${PREFIX}-list`,
  caption: `${PREFIX}-caption`,
  captionDisabled: `${PREFIX}-captionDisabled`,
  captionError: `${PREFIX}-captionError`,
  errorText: `${PREFIX}-errorText`,
  text: `${PREFIX}-text`,
} as const;

type RootType = {
  compact?: boolean;
};

const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  shouldForwardProp: prop => prop !== 'compact',
})<RootType>(({ theme: { palette, spacing, utils }, compact }) => ({
  // Wrapper (for fade masks)
  [`.${dataErrorsListClasses.wrapper}`]: {
    overflow: 'hidden',
    position: 'relative',
  },

  // Fade masks
  [`.${dataErrorsListClasses.wrapper}.${dataErrorsListClasses.wrapperFadeAfter}::after`]:
    {
      content: '""',
      display: 'block',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: 'calc(100% - 5px)',
      height: 40,
      backgroundImage: `linear-gradient(to bottom, ${alpha(
        palette.common.black,
        0,
      )}, ${alpha(palette.common.black, 1)})`,
      pointerEvents: 'none',
      zIndex: 20,
    },
  [`.${dataErrorsListClasses.wrapper}.${dataErrorsListClasses.wrapperFadeBefore}::before`]:
    {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 'calc(100% - 5px)',
      height: 40,
      backgroundImage: `linear-gradient(to bottom, ${alpha(
        palette.common.black,
        1,
      )}, ${alpha(palette.common.black, 0)})`,
      pointerEvents: 'none',
      zIndex: 20,
    },

  // Scrollable list
  [`.${dataErrorsListClasses.list}`]: {
    height: '100%',
    overflowY: 'auto',
    paddingBottom: compact ? spacing(2) : spacing(3),

    // custom scrollbar
    '::-webkit-scrollbar': { width: 5 },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': {
      maxHeight: 20,
      background: palette.grey[700],
      borderRight: 'none',
      borderLeft: 'none',
    },
    '::-webkit-scrollbar-thumb:vertical:hover': {
      background: palette.orange[500],
    },
    '::-webkit-scrollbar-thumb:vertical:active': {
      background: palette.orange[500],
    },
  },

  // Section caption
  [`.${dataErrorsListClasses.caption}`]: {
    fontSize: utils.clamp(compact ? 16 : 20, compact ? 20 : 28, 'md-4k'),
    fontWeight: 600,
    color: palette.orange[500],
    paddingTop: compact ? spacing(1) : spacing(3),
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
  },
  [`.${dataErrorsListClasses.caption}.${dataErrorsListClasses.captionDisabled}`]:
    {
      color: `${palette.grey[700]} !important`,
    },
  [`.${dataErrorsListClasses.caption}.${dataErrorsListClasses.captionError}`]: {
    color: palette.error[500],
  },

  // "NETWORK ERROR" tag
  [`.${dataErrorsListClasses.errorText}`]: {
    fontFamily: `'JetBrains Mono', monospace`,
    marginLeft: spacing(2),
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing(1),
    svg: {
      display: 'inline-block',
    },
  },

  // Row text
  [`.${dataErrorsListClasses.text}`]: {
    fontSize: utils.clamp(14, 18),
    fontWeight: 400,
    color: palette.grey[700],
    lineHeight: utils.clamp(20, 36),
  },
}));

interface Props {
  dataErrors: AppDataErrors;
  networkErrors: AppDataErrors;
  backendNetworkError: string;
  compact?: boolean;
}

export const DataErrorsList = ({
  dataErrors,
  networkErrors,
  backendNetworkError,
  compact = false,
}: Props) => {
  const [hasScroll, setHasScroll] = useState(false);
  const [isScrolled, setScrolled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setHasScroll(ref.current.scrollHeight > ref.current.clientHeight);
    }
  }, [dataErrors]);

  const handleScroll = () => {
    if (hasScroll && ref.current) {
      setScrolled(ref.current.scrollTop > 0);
    }
  };

  const { start, pause } = useAutoScroll({ ref, hasScroll });
  const onMouseEnter = () => pause();
  const onMouseLeave = () => start();

  return (
    <Root compact={compact}>
      <div
        className={[
          dataErrorsListClasses.wrapper,
          isScrolled ? dataErrorsListClasses.wrapperFadeBefore : '',
          hasScroll ? dataErrorsListClasses.wrapperFadeAfter : '',
        ].join(' ')}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          id="errorsList"
          ref={ref}
          className={dataErrorsListClasses.list}
          onScroll={handleScroll}
        >
          {Object.keys(dataErrors).map(objectTypeKey => {
            const sectionNetworkErrorsPresent = !!Object.keys(
              networkErrors[objectTypeKey] || {},
            ).length;
            const sectionDataErrorsPresent = !!Object.keys(
              dataErrors[objectTypeKey] || {},
            ).length;
            const showSection =
              sectionNetworkErrorsPresent || sectionDataErrorsPresent;

            if (!showSection) {
              return null;
            }

            const captionClass = [
              dataErrorsListClasses.caption,
              backendNetworkError ? dataErrorsListClasses.captionDisabled : '',
              sectionNetworkErrorsPresent
                ? dataErrorsListClasses.captionError
                : '',
            ].join(' ');

            return (
              <div key={objectTypeKey}>
                <div className={captionClass}>
                  {camelCaseToWords(objectTypeKey)}
                  {!backendNetworkError && sectionNetworkErrorsPresent && (
                    <span className={dataErrorsListClasses.errorText}>
                      <ErrorTriangleIcon />
                      NETWORK ERROR
                    </span>
                  )}
                </div>

                {Object.keys(dataErrors[objectTypeKey]).map(dataErrorKey => (
                  <div
                    className={dataErrorsListClasses.text}
                    key={dataErrorKey}
                  >
                    {dataErrors[objectTypeKey][dataErrorKey].label}
                    &nbsp;|&nbsp;
                    {dataErrors[objectTypeKey][dataErrorKey].errorMessages.join(
                      ', ',
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </Root>
  );
};
